import {store} from 'store/customer';
import {rotatingSet} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import * as THREE from 'three';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';

/**
 * Sets the rotating state in the store
 * @return {void}
 */
export const setRotating = () => {
    const state = store.getState();
    const isRotating = state.roomPlanner.isRotating;
    store.dispatch(rotatingSet(!isRotating));
};

/**
 * Retrieves the saved cabinet state (position, rotation) from store
 * @param {CabinetMesh} cabinetGroup - Cabinet mesh group
 * @return {object} Cabinet state containing position, rotation, and legacy position
 */
export const getCabinetState = (cabinetGroup: CabinetMesh) => {
    const cabinetPositions =
        store.getState().roomPlanner.layoutData?.layout_room_products;

    if (!cabinetPositions)
        return {position: null, rotation: null, legacyPosition: null};

    let cabinetId: string | number;
    let index: number | undefined;
    const rawMeshId = String(cabinetGroup.userData.id);
    if (!Number.isNaN(Number(rawMeshId)) && rawMeshId !== '') {
        cabinetId = Number(rawMeshId);
    } else {
        const lastDash = rawMeshId.lastIndexOf('-');
        if (lastDash !== -1) {
            const maybeIndex = rawMeshId.slice(lastDash + 1);
            if (maybeIndex !== '' && !Number.isNaN(Number(maybeIndex))) {
                cabinetId = rawMeshId.slice(0, lastDash);
                index = Number(maybeIndex);
            } else {
                cabinetId = rawMeshId;
            }
        } else {
            cabinetId = rawMeshId;
        }
    }

    const cabinetPosition = cabinetPositions?.find(
        (cabinet) =>
            String(cabinet.job_cabinet_id) === String(cabinetId) &&
            (index == undefined || index == cabinet.index)
    );

    if (!cabinetPosition)
        return {position: null, rotation: null, legacyPosition: null};

    const legacyPosition =
        cabinetPosition.x && cabinetPosition.y
            ? {
                  x: parseInt(cabinetPosition.x),
                  y: parseInt(cabinetPosition.y),
              }
            : null;

    return {
        position: cabinetPosition.position,
        rotation: cabinetPosition.rotation,
        legacyPosition,
    };
};

/**
 * Triggers wireframe mode for a cabinet if the mode is currently active
 * @param {CabinetMesh} cabinetGroup - Cabinet mesh group
 * @return {void}
 */
export const triggerIfWireframeMode = (cabinetGroup: CabinetMesh) => {
    if (store.getState().roomPlanner.isWireframeMode) {
        setTimeout(() => {
            productEvents.triggerEvents(
                cabinetGroup,
                'onToggleWireframe',
                true
            );
        }, 100);
    }
};

/**
 * Retrieves the restore position from history for undo operations
 * @param {CabinetMesh} cabinetGroup - Cabinet mesh group
 * @return {THREE.Vector3 | undefined} Restore position or undefined if not found
 */
export const getRestorePositionFromHistory = (
    cabinetGroup: CabinetMesh
): THREE.Vector3 | undefined => {
    const roomPlannerState = store.getState().roomPlanner;
    const historyForCabinet = [...roomPlannerState.historyStack]
        .reverse()
        .find((item) => {
            if (item.action !== 'CABINET_ADD' || !item.data?.previousPosition) {
                return false;
            }

            const historyId = Number(item.data.id);
            const meshId = Number(
                cabinetGroup.userData?.values?.id ??
                    cabinetGroup.userData?.values?.job_cabinet_id
            );

            return !Number.isNaN(historyId) && historyId === meshId;
        });

    const restorePosition = historyForCabinet?.data?.previousPosition;

    return restorePosition instanceof THREE.Vector3
        ? restorePosition.clone()
        : undefined;
};

/**
 * Parses the job cabinet ID from mesh userData
 * @param {string | number} id - Cabinet mesh ID
 * @return {number} Parsed cabinet ID
 */
export const parseJobCabinetIdFromMesh = (
    id: CabinetMesh['userData']['id']
): number => (typeof id === 'string' ? Number(id.split('-')[0]) : Number(id));
